/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMemo, useState } from "react"
import BigNumber from "bignumber.js"
import { addMinutes, getUnixTime } from "date-fns"
import { Link, useNavigate } from "react-router-dom"
import { parseUnits } from "viem"
import { useAccount, useConfig } from "wagmi"

import type { TransactionReturnType } from "@/domains/on-chain"
import type { CreatePairTransactionResult } from "@/features/liquidity/extract-pair-address-from-result-logs"
import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import TokenDialog from "@/components/dialogs/TokenDialog"
import { InnerCard } from "@/components/inner-card"
import { InnerCardWarning } from "@/components/inner-card-warning"
import { Separator } from "@/components/separator"
import { Switch } from "@/components/switch"
import { TokenInput } from "@/components/token-input"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { LiquidityPoolTypeCard } from "@/domains/common/components/liquidity-pool-type-card"
import { MessageBox } from "@/domains/common/components/message-box"
import { PageContainer } from "@/domains/common/layout/page-container"
import { weiValueToBigNumber } from "@/domains/core"
import { CreateIcon, DollarIcon, ExternalLinkIcon, WalletIcon } from "@/domains/icons"
import { createLiquidityPoolInteraction } from "@/domains/interactions"
import { clampNumber, cn, dollars, trimEthAddress } from "@/domains/library"
import { tokenStoresToTokens } from "@/domains/tokens/utils/token-store-to-token"
import { INSUFFICIENT_BALANCE_ERROR } from "@/features/liquidity/constants"
import { extractPairAddressFromResultLogs } from "@/features/liquidity/extract-pair-address-from-result-logs"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useCreateLiquidityPoolLens } from "@/hooks/use-create-liquidity-pool-lens"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useNativeToken } from "@/hooks/use-native-token"
import useScanner from "@/hooks/use-scanner"
import { useTokensWithBalances } from "@/hooks/use-tokens-with-balances"
import { useWrappedToken } from "@/hooks/use-wrapped-token"
import useEventEmitter from "@/hooks/useEventEmitter"

const deadlineMinutes = 20

export default function CreatePage() {
  const navigate = useNavigate()
  const { address: account } = useAccount()
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const nativeToken = useNativeToken()
  const wrappedToken = useWrappedToken()
  const governanceToken = useGovernanceToken()
  const [token0, setToken0] = useState(wrappedToken)
  const [token1, setToken1] = useState(governanceToken)
  const scanner = useScanner()
  const scaleApp = useScaleApplication()

  const chainId = chainConfig.chainId

  const { tokens } = useTokensWithBalances()

  // Make pool state
  const [amountToken0, setAmountToken0] = useState("0")
  const [amountToken1, setAmountToken1] = useState("0")
  const [errorToken0, setErrorToken0] = useState<string | null>(null)
  const [errorToken1, setErrorToken1] = useState<string | null>(null)
  const [pairType, setPairType] = useState<"volatile" | "stable">("volatile")

  const [createdPair, setCreatedPair] = useState(false)

  const isStable = pairType === "stable"

  const { lens, createLiquidityPoolLensQuery } = useCreateLiquidityPoolLens({
    account: account!,
    chainId,
    config,
    token0,
    token1,
    isStable,
    routerAddress: chainConfig.equalizer.contracts.router,
    pairFactoryAddress: chainConfig.equalizer.contracts.factory,
  })

  const amountToken0BN = BigNumber(amountToken0 ?? 0)
  const amountToken1BN = BigNumber(amountToken1 ?? 0)

  const token0AvailableAmountBN = weiValueToBigNumber({
    wei: lens?.token0?.wei ?? 0n,
    decimals: token0?.decimals ?? 0,
  })
  const token1AvailableAmountBN = weiValueToBigNumber({
    wei: lens?.token1?.wei ?? 0n,
    decimals: token1?.decimals ?? 0,
  })

  const hasSufficientBalanceToken0 = amountToken0BN.lte(token0AvailableAmountBN)
  const hasSufficientBalanceToken1 = amountToken1BN.lte(token1AvailableAmountBN)
  const token0DollarValueBN: BigNumber = amountToken0BN.times(token0.priceUsd ?? 0)
  const token1DollarValueBN: BigNumber = amountToken1BN.times(token1.priceUsd ?? 0)

  const pairExists = Boolean(lens?.pairExists)
  const pairExistsAddress = lens?.pairExistsAddress

  const canMakeLiquidity = amountToken0BN.gt(0) && amountToken1BN.gt(0) && !errorToken0 && !errorToken1 && !pairExists

  //#region update token input amount handlers
  const updateToken0Amount = (newAmount: string) => {
    if (!newAmount) {
      setAmountToken0("0")
      setErrorToken0(null)
      return
    }
    setAmountToken0(newAmount)

    const newAmountBI = parseUnits(newAmount, token0.decimals)
    const balanceToken0 = lens?.token0?.wei ?? 0n
    setErrorToken0(newAmountBI > balanceToken0 ? "Insufficient balance" : null)
  }

  const updateToken1Amount = (newAmount: string) => {
    if (!newAmount) {
      setAmountToken1("0")
      setErrorToken1(null)
      return
    }
    setAmountToken1(newAmount)

    const newAmountBI = parseUnits(newAmount, token1.decimals)
    const balanceToken1 = lens?.token1?.wei ?? 0n
    setErrorToken1(newAmountBI > balanceToken1 ? "Insufficient balance" : null)
  }

  //#endregion

  //#region - create liquidity pair
  const createLiquidityPairAction = async () => {
    try {
      // create min amounts taking a 1% slippage into account
      const token0AmountMinBN = amountToken0BN.minus(amountToken0BN.times(0.01))
      const token1AmountMinBN = amountToken1BN.minus(amountToken1BN.times(0.01))

      const token0AmountBI = parseUnits(amountToken0BN.toFixed(18), token0.decimals)
      const token1AmountBI = parseUnits(amountToken1BN.toFixed(18), token1.decimals)
      const token0AmountMinBI = parseUnits(token0AmountMinBN.toFixed(18), token0.decimals)
      const token1AmountMinBI = parseUnits(token1AmountMinBN.toFixed(18), token1.decimals)

      const deadline = getUnixTime(addMinutes(new Date(), clampNumber(deadlineMinutes, 1, 60)))
      const result = await createLiquidityPoolInteraction({
        account: account!,
        chainId,
        config,
        nativeToken,
        wrappedToken,
        token0,
        token1,
        token0AmountBI,
        token1AmountBI,
        token0AmountMinBI,
        token1AmountMinBI,
        isStable,
        deadline,
        emitter,
        lens: lens!,
        approvalMode: scaleApp?.Settings.approvalMode,
        routerAddress: chainConfig.equalizer.contracts.router,
        onCreatePoolBegin,
        onCreatePoolSuccess,
      })

      console.log("createLiquidityPairAction->result", result)
    } catch (error) {
      console.error("createLiquidityPairAction->error", error)
    }
  }
  //#endregion

  //#region - create liquidity pair success
  const onCreatePoolSuccess = (result: TransactionReturnType) => {
    const pairAddressFromLogs = extractPairAddressFromResultLogs(result as unknown as CreatePairTransactionResult)
    console.log("pairAddressFromLogs", pairAddressFromLogs)

    setCreatedPair(true)
    if (pairAddressFromLogs) {
      scaleApp.PendingCreation.setPendingClassicLp({
        pairAddress: pairAddressFromLogs,
        token0Symbol: token0.symbol ?? "Token0",
        token1Symbol: token1.symbol ?? "Token1",
        chainId,
      })
      navigate("/liquidity/creating", { replace: true })
    } else {
      void createLiquidityPoolLensQuery.refetch()
    }
  }

  const onCreatePoolBegin = () => {
    setCreatedPair(false)
  }
  //#endregion

  //#region - we do not want to show cl-farm or junk tokens in the create pool form
  const filteredTokens = useMemo(() => {
    const excludedTags = ["cl-farm", "junk"]
    return tokens.filter((token: any) => !token?.tags?.some((tag: string) => excludedTags.includes(tag)))
  }, [tokens])

  const quickSelectTokens = useMemo(
    () => tokenStoresToTokens(scaleApp.Tokens.QuickSelect.tokens),
    [scaleApp.Tokens.QuickSelect.tokens],
  )

  //#endregion

  return (
    <TooltipProvider>
      <PageContainer>
        <div className="grid w-full grid-cols-1 gap-4 text-t100 md:grid-cols-2">
          <Card.Container className="flex w-full flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 md:max-w-xl lg:p-6">
            <Card.Header>
              <Card.HeaderIconContainer>
                <CreateIcon size={24} className="text-primary-500" />
              </Card.HeaderIconContainer>

              <div className="flex w-full flex-col">
                <Card.HeaderTitle>Create</Card.HeaderTitle>
                <Card.HeaderDescription>Provide Liquidity on Scale and reap its rewards.</Card.HeaderDescription>
              </div>
            </Card.Header>
            <Separator
              className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
          after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
          after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
              decorative
            />
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-row items-center gap-3 text-sm text-t200">
                <p>Volatile</p>
                <Switch
                  id="volatile-stable"
                  checked={isStable}
                  onCheckedChange={() => setPairType(isStable ? "volatile" : "stable")}
                />
                <p>Stable</p>
              </div>
              <div className="flex flex-row items-center gap-3 text-sm">
                <LiquidityPoolTypeCard
                  pool={{ pairType: pairType, isStable: isStable, displayType: isStable ? "Stable" : "Volatile" }}
                />
              </div>
            </div>
            <InnerCard hasWarning={!hasSufficientBalanceToken0}>
              <div className="flex flex-row items-center justify-between">
                <TokenInput.Value
                  disabled={createdPair || pairExists}
                  value={amountToken0}
                  decimals={token0.decimals}
                  symbol={token0.symbol}
                  onValueChange={updateToken0Amount}
                  className="h-fit min-w-[180px] flex-grow p-0"
                  placeholder="0"
                />
                <TokenDialog
                  account={account!}
                  chainConfig={chainConfig}
                  tokens={tokens}
                  quickSelectTokens={quickSelectTokens}
                  governanceToken={governanceToken}
                  nativeToken={nativeToken}
                  wrappedNativeToken={wrappedToken}
                  onTokenSelect={(v) => {
                    if (v !== null) setToken0(v)
                  }}
                  showLocalAssets={true}
                  className="mx-auto flex flex-none flex-row items-center"
                >
                  <TokenInput.SelectableToken token={token0} />
                </TokenDialog>
              </div>

              {/* balances */}
              <div className="flex w-full flex-row items-center justify-between text-xs">
                <ShowWhenTrue when={hasSufficientBalanceToken0}>
                  <div className="flex items-center gap-1 text-sm text-t200">
                    <DollarIcon size={14} className="text-t200" />
                    <p>~{dollars(token0DollarValueBN.toNumber())}</p>
                  </div>
                </ShowWhenTrue>
                <ShowWhenTrue when={!hasSufficientBalanceToken0}>
                  <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
                </ShowWhenTrue>
                <TokenInput.Balance
                  balance={lens?.token0.value ?? "0"}
                  label={<WalletIcon size={14} className="text-t200" />}
                  decimals={6}
                  className={cn(errorToken0 && "text-warning")}
                />
              </div>

              <TokenInput.PercentageButtons className="grid w-full grid-cols-5 items-center gap-2">
                <TokenInput.PercentageButtonBN
                  label="0%"
                  token={token0}
                  hideTooltip
                  onClick={() => updateToken0Amount("0")}
                  amount={BigNumber(0)}
                  variant="neutral"
                />
                <TokenInput.PercentageButtonBN
                  label="25%"
                  token={token0}
                  onClick={updateToken0Amount}
                  amount={token0AvailableAmountBN.times(BigNumber(0.25))}
                  variant="neutral"
                />
                <TokenInput.PercentageButtonBN
                  label="50%"
                  token={token0}
                  onClick={updateToken0Amount}
                  amount={token0AvailableAmountBN.times(BigNumber(0.5))}
                  variant="neutral"
                />
                <TokenInput.PercentageButtonBN
                  label="75%"
                  token={token0}
                  onClick={updateToken0Amount}
                  amount={token0AvailableAmountBN.times(BigNumber(0.75))}
                  variant="neutral"
                />
                <TokenInput.PercentageButtonBN
                  label="MAX"
                  token={token0}
                  onClick={updateToken0Amount}
                  amount={token0AvailableAmountBN}
                  variant="neutral"
                />
              </TokenInput.PercentageButtons>
            </InnerCard>

            {/* buy token */}
            <InnerCard hasWarning={!hasSufficientBalanceToken1}>
              <div className="flex flex-row items-center justify-between">
                <TokenInput.Value
                  disabled={createdPair || pairExists}
                  value={amountToken1}
                  decimals={token1.decimals}
                  symbol={token1.symbol}
                  onValueChange={updateToken1Amount}
                  className="h-fit p-0 placeholder:text-t200  "
                  placeholder="0"
                />

                <TokenDialog
                  account={account!}
                  chainConfig={chainConfig}
                  tokens={tokens}
                  quickSelectTokens={quickSelectTokens}
                  governanceToken={governanceToken}
                  nativeToken={nativeToken}
                  wrappedNativeToken={wrappedToken}
                  onTokenSelect={(v) => {
                    if (v !== null) setToken1(v)
                  }}
                  showLocalAssets={true}
                  className="mx-auto flex flex-none flex-row items-center"
                >
                  <TokenInput.SelectableToken
                    token={token1}
                    //  @ts-expect-error Was working before
                    onSelectToken={(v) => {
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                      if (v !== null) setToken1(v)
                    }}
                    chain={chainConfig}
                  />
                </TokenDialog>
              </div>

              {/* balances */}
              <div className="flex w-full flex-row items-center justify-between text-xs">
                <ShowWhenTrue when={hasSufficientBalanceToken1}>
                  <div className="flex items-center gap-1 text-sm text-t200">
                    <DollarIcon size={14} className="text-t200" />
                    <p>~{dollars(token1DollarValueBN.toNumber())}</p>
                  </div>
                </ShowWhenTrue>
                <ShowWhenTrue when={!hasSufficientBalanceToken1}>
                  <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
                </ShowWhenTrue>

                <TokenInput.Balance
                  balance={lens?.token1.value ?? "0"}
                  label={<WalletIcon size={14} className="text-t200" />}
                  decimals={6}
                />
              </div>
              <TokenInput.PercentageButtons className="grid w-full grid-cols-5 items-center gap-2">
                <TokenInput.PercentageButtonBN
                  label="0%"
                  token={token1}
                  hideTooltip
                  onClick={() => updateToken1Amount("0")}
                  amount={BigNumber(0)}
                  variant="neutral"
                />
                <TokenInput.PercentageButtonBN
                  label="25%"
                  token={token1}
                  onClick={updateToken1Amount}
                  amount={token1AvailableAmountBN.times(BigNumber(0.25))}
                  variant="neutral"
                />
                <TokenInput.PercentageButtonBN
                  label="50%"
                  token={token1}
                  onClick={updateToken1Amount}
                  amount={token1AvailableAmountBN.times(BigNumber(0.5))}
                  variant="neutral"
                />
                <TokenInput.PercentageButtonBN
                  label="75%"
                  token={token1}
                  onClick={updateToken1Amount}
                  amount={token1AvailableAmountBN.times(BigNumber(0.75))}
                  variant="neutral"
                />
                <TokenInput.PercentageButtonBN
                  label="MAX"
                  token={token1}
                  onClick={updateToken1Amount}
                  amount={token1AvailableAmountBN}
                  variant="neutral"
                />
              </TokenInput.PercentageButtons>
            </InnerCard>
            <Button disabled={!canMakeLiquidity} variant={"primary"} onClick={() => createLiquidityPairAction()}>
              {isStable ? "Create Stable Pool" : "Create Volatile Pool"}
            </Button>
          </Card.Container>
          <ShowWhenTrue when={pairExists}>
            <div>
              <MessageBox variant={"error"}>
                <p>
                  This pair already exists, if you would like to add liquidity to it, click{" "}
                  <Link href={`/liquidity/${pairExistsAddress}/add`} target="_self" className="underline">
                    here.
                  </Link>
                </p>
                <a
                  className="mt-4 flex flex-row items-center gap-1"
                  href={scanner.makeTokenUrl(pairExistsAddress as string)}
                  target="_blank"
                >
                  View pair {trimEthAddress(pairExistsAddress as string, 6, 6)} on {scanner.name}
                  <ExternalLinkIcon size={12} className="cursor-pointer text-t200" />
                </a>
              </MessageBox>
            </div>
          </ShowWhenTrue>
        </div>
      </PageContainer>
    </TooltipProvider>
  )
}

/*

CREATE PAIR RESULT

result= {
  success: true,
  simulatedResults: [1000000000000000000,1000000,999999999000],
  transaction: {
    blockHash: "0x0004e34500000a67215f5a3c1aa3f4aa91f2ac728fa642e48e52b1532967f72e",
    blockNumber: 98933088n,
    chainId: 250,
    contractAddress: null,
    cumulativeGasUsed: 6113299n,
    effectiveGasPrice: 7000000000n,
    from : "0x4773ceef765f02169bbbe6180657a49ee58ac96b",
    gasUsed:4849124n,
    logs: [          
    ],
    success: true,
    to: "0x33da53f731458d6bc970b0c5fcbb0b3db4aaa470",
    transactionHash: "0xec7691f3ac37134f36ae2951d0e15497870be8fc8b886ed16d6588ea1d48fca0",
    transactionIndex: 2
    type: "legacy"

  },
  tx: "0xec7691f3ac37134f36ae2951d0e15497870be8fc8b886ed16d6588ea1d48fca0"
}

log[4] = {
  topics: [
    0: "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef",
    1: "0x0000000000000000000000004773ceef765f02169bbbe6180657a49ee58ac96b",
    2: "0x000000000000000000000000d31226071bdfa64741acd1dbc1885377a71d58b3" // new pair address
  ]
}

log[6] = {
  topics: [
    0: "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef",
    1: "0x0000000000000000000000004773ceef765f02169bbbe6180657a49ee58ac96b",
    2: "0x000000000000000000000000d31226071bdfa64741acd1dbc1885377a71d58b3" // new pair address
  ]
}

takes a while for pairExists to update, we should directly refetch that lens afterwards, to get the new address..


Then after a minute or so:
pairExists: true
pairExistsAddress: 0xD31226071BdFA64741AcD1DbC1885377a71D58b3
undefined
*/
